import React from 'react';
import PropTypes from 'prop-types';
import { BoxBlock } from '@components/page/BlockRenderer';
import { useAppContext } from '@/context/AppContext';
import { FADE_DURATION } from '../imageViewerUtils';

const JUSTIFY = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
const ALIGN = { start: 'flex-start', center: 'center', end: 'flex-end' };

/** A block's width and place across the text page. */
export function readingBoxStyle(box) {
    const width = box.phoneText?.w;
    return {
        background: box.bg || undefined,
        padding: box.pad ? `${Math.min(box.pad, 32)}px` : undefined,
        ...(width && { width: `${width}%`, alignSelf: ALIGN[box.phoneText.align] || ALIGN.start }),
    };
}

/** The reading column's layout, shared with the editor's copy of the page. */
export const READING_COLUMN_CLASS = 'mx-auto flex min-h-full w-full max-w-prose flex-col gap-8 px-6 py-16';

export function readingColumnStyle(valign) {
    return { justifyContent: JUSTIFY[valign] || JUSTIFY.center };
}

/**
 * On a phone, the blocks placed on a slideshow slide, after its pictures, at
 * reading size and in reading order. Scrolls when they don't fit.
 */
export default function SpreadReadingSlide({ boxes, valign = 'center', page, pages, files, theme }) {
    const { content, theme: siteTheme } = useAppContext();
    const blockProps = {
        page,
        pages: pages || content?.pages || {},
        files: files || content?.files || {},
        theme: theme || siteTheme,
    };
    return (
        <div
            data-reading-slide
            className="h-full w-full overflow-y-auto bg-background"
            style={{ touchAction: 'pan-y', transition: `background-color ${FADE_DURATION}ms ease-in-out` }}
        >
            <div className={READING_COLUMN_CLASS} style={readingColumnStyle(valign)}>
                {boxes.map((box) => (
                    <div key={box.id} style={readingBoxStyle(box)}>
                        <BoxBlock block={box.block} {...blockProps} />
                    </div>
                ))}
            </div>
        </div>
    );
}

SpreadReadingSlide.propTypes = {
    /** Boxes from lib/slideshow/boxes, already in reading order. */
    boxes: PropTypes.array.isRequired,
    /** Where the blocks sit when they are shorter than the screen. */
    valign: PropTypes.oneOf(['top', 'center', 'bottom']),
    page: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    theme: PropTypes.object,
};
